// Single-page deal edit session — namespaced per dealId.
// Edit flow does NOT touch the drafts API. Only LS + IDB.
//
// LS shape per deal:
//   {
//     dealId,
//     savedAt,            // ms epoch — for stale eviction
//     fields,             // strip File/base64; safe-to-serialize subset
//     imageMeta: { hasImage: boolean }
//   }
//
// IDB stores the in-flight File so refresh can rehydrate.

const LS_PREFIX = "merchant_deal_edit:";
const IDB_KEY_PREFIX = "merchant_deal_edit_image:";
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

const DB_NAME = "sumoeats_merchant_drafts";
const DB_VERSION = 1;
const STORE_NAME = "draft_assets";

const lsKey = (dealId) => `${LS_PREFIX}${dealId}`;
const idbKey = (dealId) => `${IDB_KEY_PREFIX}${dealId}`;

const hasIndexedDb = () =>
    typeof window !== "undefined" && typeof window.indexedDB !== "undefined";

const openDb = () => {
    if (!hasIndexedDb()) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
        try {
            const req = window.indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error || new Error("IDB open failed"));
        } catch (err) {
            reject(err);
        }
    });
};

const runStore = async (mode, exec) => {
    const db = await openDb();
    if (!db) return null;
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (cb, val) => {
            if (settled) return;
            settled = true;
            try { db.close(); } catch { /* noop */ }
            cb(val);
        };
        try {
            const tx = db.transaction(STORE_NAME, mode);
            const store = tx.objectStore(STORE_NAME);
            const req = exec(store);
            req.onsuccess = () => finish(resolve, req.result ?? null);
            req.onerror = () => finish(reject, req.error || new Error("IDB request failed"));
            tx.onabort = () => finish(reject, tx.error || new Error("IDB tx aborted"));
            tx.onerror = () => finish(reject, tx.error || new Error("IDB tx failed"));
        } catch (err) {
            finish(reject, err);
        }
    });
};

// -------------------- LS API --------------------

export const readEditSession = (dealId) => {
    if (!dealId) return null;
    try {
        const raw = localStorage.getItem(lsKey(dealId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        // Stale eviction
        if (parsed.savedAt && Date.now() - parsed.savedAt > STALE_AFTER_MS) {
            localStorage.removeItem(lsKey(dealId));
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

export const writeEditSession = (dealId, patch) => {
    if (!dealId) return;
    try {
        const current = readEditSession(dealId) || { dealId };
        const next = {
            ...current,
            ...patch,
            dealId,
            savedAt: Date.now(),
        };
        localStorage.setItem(lsKey(dealId), JSON.stringify(next));
    } catch {
        /* quota or serialization — non-fatal */
    }
};

export const clearEditSession = (dealId) => {
    if (!dealId) return;
    try {
        localStorage.removeItem(lsKey(dealId));
    } catch {
        /* noop */
    }
};

// Used by logout — wipe ALL pending edit sessions.
export const clearAllEditSessions = () => {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (k && k.startsWith(LS_PREFIX)) keys.push(k);
        }
        keys.forEach((k) => localStorage.removeItem(k));
    } catch {
        /* noop */
    }
};

// -------------------- IDB API --------------------

export const saveEditImage = async (dealId, file) => {
    if (!dealId || !(file instanceof Blob)) return false;
    try {
        await runStore("readwrite", (store) =>
            store.put(
                {
                    blob: file,
                    name: file.name || "deal-image",
                    type: file.type || "application/octet-stream",
                    updatedAt: Date.now(),
                },
                idbKey(dealId),
            ),
        );
        return true;
    } catch {
        return false;
    }
};

export const readEditImage = async (dealId) => {
    if (!dealId) return null;
    try {
        const record = await runStore("readonly", (store) => store.get(idbKey(dealId)));
        if (!record || !(record.blob instanceof Blob)) return null;
        const blob = record.blob;
        const name = record.name || "deal-image";
        const type = record.type || blob.type || "application/octet-stream";
        const lastModified = Number(record.updatedAt || Date.now());
        if (typeof File !== "undefined") {
            return new File([blob], name, { type, lastModified });
        }
        return blob;
    } catch {
        return null;
    }
};

export const clearEditImage = async (dealId) => {
    if (!dealId) return false;
    try {
        await runStore("readwrite", (store) => store.delete(idbKey(dealId)));
        return true;
    } catch {
        return false;
    }
};

// Used by logout — best-effort wipe of all per-deal image entries.
export const clearAllEditImages = async () => {
    try {
        const db = await openDb();
        if (!db) return false;
        return await new Promise((resolve) => {
            try {
                const tx = db.transaction(STORE_NAME, "readwrite");
                const store = tx.objectStore(STORE_NAME);
                const req = store.openCursor();
                req.onsuccess = () => {
                    const cursor = req.result;
                    if (!cursor) {
                        try { db.close(); } catch { /* noop */ }
                        resolve(true);
                        return;
                    }
                    const k = cursor.key;
                    if (typeof k === "string" && k.startsWith(IDB_KEY_PREFIX)) {
                        cursor.delete();
                    }
                    cursor.continue();
                };
                req.onerror = () => {
                    try { db.close(); } catch { /* noop */ }
                    resolve(false);
                };
            } catch {
                try { db.close(); } catch { /* noop */ }
                resolve(false);
            }
        });
    } catch {
        return false;
    }
};
